import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Wifi, WifiOff, Trash2, Loader2 } from 'lucide-react'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'

interface ActivityFeedProps {
  events: ToolCallEvent[]
  connected: boolean
  connecting: boolean
  onClear: () => void
  compact?: boolean
}

function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'started':
      return (
        <Badge
          variant="outline"
          className="bg-blue-500/10 text-blue-500 border-blue-500/20 text-xs px-1.5 py-0"
        >
          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
          Running
        </Badge>
      )
    case 'completed':
      return (
        <Badge
          variant="outline"
          className="bg-green-500/10 text-green-500 border-green-500/20 text-xs px-1.5 py-0"
        >
          ✓ Completed
        </Badge>
      )
    case 'failed':
      return (
        <Badge
          variant="outline"
          className="bg-red-500/10 text-red-500 border-red-500/20 text-xs px-1.5 py-0"
        >
          ✗ Failed
        </Badge>
      )
    default:
      return (
        <Badge variant="outline" className="text-xs px-1.5 py-0">
          {status}
        </Badge>
      )
  }
}

function formatResultPreview(result: unknown): string {
  if (result === null || result === undefined) return ''
  if (typeof result === 'string') return result
  try {
    return JSON.stringify(result, null, 2)
  } catch {
    return String(result)
  }
}

export function ActivityFeed({
  events,
  connected,
  connecting,
  onClear,
  compact,
}: ActivityFeedProps) {
  // Compact mode: no card wrapper, smaller styling
  if (compact) {
    return (
      <div className="h-full flex flex-col">
        {/* Compact header */}
        <div className="flex items-center justify-between px-1 mb-1">
          <div className="flex items-center gap-1.5 text-xs">
            {connecting ? (
              <Loader2 className="w-3 h-3 text-yellow-500 animate-spin" />
            ) : connected ? (
              <Wifi className="w-3 h-3 text-green-500" />
            ) : (
              <WifiOff className="w-3 h-3 text-red-500" />
            )}
            <span className="text-muted-foreground">{events.length} events</span>
          </div>
          {events.length > 0 && (
            <Button variant="ghost" size="sm" onClick={onClear} className="h-6 px-1.5 text-xs">
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </div>

        <ScrollArea className="flex-1">
          {events.length === 0 ? (
            <div className="text-center text-muted-foreground py-4 text-xs">No activity yet</div>
          ) : (
            <div className="space-y-1 pr-2">
              {events.map((event, index) => (
                <CompactEventRow
                  key={event.call_id || `${event.timestamp}-${index}`}
                  event={event}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    )
  }

  // Full mode: with card wrapper
  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3 px-4">
        <CardTitle className="flex items-center gap-2 text-base">
          Activity Feed
          {connecting ? (
            <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />
          ) : connected ? (
            <Wifi className="w-4 h-4 text-green-500" />
          ) : (
            <WifiOff className="w-4 h-4 text-red-500" />
          )}
          <span className="text-xs text-muted-foreground font-normal">({events.length})</span>
        </CardTitle>
        {events.length > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear} className="h-7 px-2">
            <Trash2 className="w-3 h-3 mr-1" />
            Clear
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex-1 min-h-0 px-4 pb-4">
        <ScrollArea className="h-[400px]">
          {events.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <p className="text-sm">No activity yet.</p>
              <p className="text-xs mt-2">
                Tool calls from MCP clients will appear here in real-time.
              </p>
            </div>
          ) : (
            <div className="space-y-2 pr-3">
              {events.map((event, index) => (
                <div
                  key={event.call_id || `${event.timestamp}-${index}`}
                  className="p-2 rounded-md bg-muted/50 border border-border/50"
                >
                  {/* Header row: tool name + status */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-medium truncate">
                      {event.data.tool_name}
                    </span>
                    {getStatusBadge(event.data.status)}
                  </div>

                  {/* Meta row: time + response time */}
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1">
                    <span>{formatTimestamp(event.timestamp)}</span>
                    {event.data.response_time_ms && <span>{event.data.response_time_ms}ms</span>}
                  </div>

                  {/* Error message */}
                  {event.data.error && (
                    <p className="mt-1.5 text-[10px] text-red-500 font-mono break-words">
                      {event.data.error}
                    </p>
                  )}

                  {/* Collapsible parameters */}
                  {event.data.params && Object.keys(event.data.params).length > 0 && (
                    <details className="mt-1.5">
                      <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground">
                        Parameters
                      </summary>
                      <pre className="mt-1 text-[10px] font-mono bg-background p-1.5 rounded whitespace-pre-wrap break-all max-h-24 overflow-y-auto">
                        {JSON.stringify(event.data.params, null, 2)}
                      </pre>
                    </details>
                  )}

                  {/* Collapsible result preview */}
                  {event.data.result_preview && (
                    <details className="mt-1.5">
                      <summary className="text-[10px] text-green-600 dark:text-green-400 cursor-pointer hover:text-green-700 dark:hover:text-green-300">
                        Result Preview
                      </summary>
                      <pre className="mt-1 text-[10px] font-mono bg-green-500/5 border border-green-500/20 p-1.5 rounded whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                        {formatResultPreview(event.data.result_preview)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  )
}

// Compact event row for bottom panel
function CompactEventRow({ event }: { event: ToolCallEvent }) {
  return (
    <div className="flex items-center gap-2 px-1.5 py-1 rounded-sm hover:bg-accent/50 text-xs">
      {/* Status indicator */}
      {event.data.status === 'started' ? (
        <Loader2 className="w-3 h-3 text-blue-500 animate-spin shrink-0" />
      ) : event.data.status === 'completed' ? (
        <span className="w-3 h-3 flex items-center justify-center text-green-500 shrink-0">✓</span>
      ) : (
        <span className="w-3 h-3 flex items-center justify-center text-red-500 shrink-0">✗</span>
      )}

      {/* Tool name */}
      <span className="font-mono truncate flex-1">{event.data.tool_name}</span>

      {/* Time */}
      <span className="text-[10px] text-muted-foreground shrink-0">
        {formatTimestamp(event.timestamp)}
      </span>
    </div>
  )
}
